/**
 * ShapeValue — defensive renderers for non-scalar tag shapes (P3.4).
 *
 * Track B emits two non-scalar wire shapes in `readings.value_jsonb`:
 *   - shape:'array'  → data_kind 'categorical_distribution', a bucket map
 *     like { "0": 12.4, "1": 9.1, ... } or a labelled map { co2: 3.1, ... }.
 *   - shape:'batch'  → data_kind 'event_stream', a flat record like
 *     { field_a: 12.5, field_b: "PASS", ... }.
 *
 * Track A renders these as a mini sparkline/histogram (array) and a compact
 * key/value table (batch). We read DEFENSIVELY: the jsonb may arrive under a
 * few different keys (the port-values / latest endpoints are extended by
 * Track B in parallel), and may be absent entirely (B not merged yet). In
 * every uncertain case we degrade to `null` so callers can fall back to
 * "no live value" — never crash.
 */

import { useMemo } from "react";

/**
 * Pull a jsonb payload out of an arbitrary live-value object without
 * depending on Track B's final field name. We probe the documented +
 * likely keys (`value_jsonb`, `valueJsonb`, `jsonb`, `value_json`) and
 * accept either a parsed object or a JSON string.
 */
export function extractJsonb(src: unknown): Record<string, unknown> | null {
  if (src == null || typeof src !== "object") return null;
  const obj = src as Record<string, unknown>;
  const candidates = ["value_jsonb", "valueJsonb", "jsonb", "value_json", "payload"];
  for (const key of candidates) {
    const raw = obj[key];
    const parsed = coerceObject(raw);
    if (parsed) return parsed;
  }
  return null;
}

function coerceObject(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s || (s[0] !== "{" && s[0] !== "[")) return null;
    try {
      const p = JSON.parse(s);
      return p && typeof p === "object" ? (p as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === "object") return raw as Record<string, unknown>;
  return null;
}

export interface DistributionBucket {
  label: string;
  value: number;
}

/**
 * Normalise a categorical_distribution jsonb into ordered buckets.
 * Tolerates:
 *   - a flat map { "0": 12, "1": 9 } or { label: count }
 *   - a nested `{ buckets: {...} }` / `{ distribution: {...} }` wrapper
 *   - a `{ counts: [...] , labels?: [...] }` array form
 * Numeric-looking keys are sorted numerically so spectra read left→right.
 */
export function toDistribution(jsonb: Record<string, unknown> | null): DistributionBucket[] {
  if (!jsonb) return [];

  // Array form: { counts:[...], labels?:[...] } or { values:[...] }
  const arr = firstArray(jsonb, ["counts", "values", "buckets", "bins", "data"]);
  if (arr) {
    const labels = firstArray(jsonb, ["labels", "keys", "bins"]);
    return arr
      .map((v, i) => ({
        label: String(labels?.[i] ?? i),
        value: toNum(v),
      }))
      .filter((b) => b.value != null) as DistributionBucket[];
  }

  // Map form, possibly wrapped under buckets/distribution/histogram.
  const map = firstObject(jsonb, ["buckets", "distribution", "histogram", "dist"]) ?? jsonb;
  const entries = Object.entries(map)
    .map(([label, v]) => ({ label, value: toNum(v) }))
    .filter((b) => b.value != null) as DistributionBucket[];

  const allNumeric = entries.every((e) => /^-?\d+(\.\d+)?$/.test(e.label));
  if (allNumeric) entries.sort((a, b) => Number(a.label) - Number(b.label));
  return entries;
}

/**
 * Flatten an event_stream record into displayable key/value rows. Accepts
 * the bare record, or a `{ record: {...} }` / `{ fields: {...} }` / first
 * element of a `{ records:[...] }` newest-first list.
 */
export function toRecord(jsonb: Record<string, unknown> | null): Array<{ key: string; value: string }> {
  if (!jsonb) return [];
  let rec: Record<string, unknown> | null =
    firstObject(jsonb, ["record", "fields", "event", "latest"]) ?? null;
  if (!rec) {
    const list = firstArray(jsonb, ["records", "events", "rows"]);
    if (list && list.length > 0 && typeof list[0] === "object" && list[0] != null) {
      rec = list[0] as Record<string, unknown>;
    }
  }
  if (!rec) rec = jsonb;
  return Object.entries(rec)
    .filter(([, v]) => v == null || typeof v !== "object")
    .map(([key, v]) => ({ key, value: v == null ? "—" : String(v) }));
}

function toNum(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function firstArray(obj: Record<string, unknown>, keys: string[]): unknown[] | null {
  for (const k of keys) {
    if (Array.isArray(obj[k])) return obj[k] as unknown[];
  }
  return null;
}

function firstObject(
  obj: Record<string, unknown>,
  keys: string[],
): Record<string, unknown> | null {
  for (const k of keys) {
    const v = obj[k];
    if (v != null && typeof v === "object" && !Array.isArray(v)) {
      return v as Record<string, unknown>;
    }
  }
  return null;
}

/**
 * Mini histogram/sparkline for a categorical distribution. Pure inline SVG
 * (no recharts dependency) so it stays cheap inside dense tile grids.
 */
export function ArraySparkline({
  buckets,
  height = 40,
  className,
  color = "rgb(var(--color-brand))",
}: {
  buckets: DistributionBucket[];
  height?: number;
  className?: string;
  color?: string;
}) {
  const max = useMemo(
    () => buckets.reduce((m, b) => Math.max(m, b.value), 0) || 1,
    [buckets],
  );
  if (buckets.length === 0) return null;

  const n = buckets.length;
  const gap = n > 24 ? 0.5 : 1.5;
  const barW = Math.max(1, (100 - gap * (n - 1)) / n);

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      width="100%"
      height={height}
      className={className}
      role="img"
      aria-label={`Distribution of ${n} buckets`}
    >
      {buckets.map((b, i) => {
        const h = (b.value / max) * 100;
        const x = i * (barW + gap);
        return (
          <rect
            key={b.label + i}
            x={x}
            y={100 - h}
            width={barW}
            height={Math.max(h, 0.5)}
            fill={color}
            opacity={0.85}
          >
            <title>{`${b.label}: ${b.value}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

/**
 * Compact key/value table for an event_stream / batch record. Caps rows so
 * a wide lab report doesn't blow out the tile; the overflow count is noted.
 */
export function BatchKeyValueTable({
  rows,
  max = 8,
  className,
}: {
  rows: Array<{ key: string; value: string }>;
  max?: number;
  className?: string;
}) {
  if (rows.length === 0) return null;
  const shown = rows.slice(0, max);
  const overflow = rows.length - shown.length;
  return (
    <div className={className}>
      <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-0.5">
        {shown.map((r) => (
          <div key={r.key} className="contents">
            <dt className="truncate text-[10px] text-on-surface-muted" title={r.key}>
              {r.key.replace(/_/g, " ")}
            </dt>
            <dd className="truncate text-right font-mono text-[10px] text-on-surface" title={r.value}>
              {r.value}
            </dd>
          </div>
        ))}
      </dl>
      {overflow > 0 && (
        <p className="mt-0.5 text-right text-[9px] text-on-surface-muted">+{overflow} more</p>
      )}
    </div>
  );
}

/**
 * One-stop renderer: given a slot's data_kind + a live-value object, render
 * the right non-scalar visual, or return null when there's nothing live to
 * show (so the caller can fall back to "no live value").
 */
export function ShapeValue({
  dataKind,
  live,
  histogramColor,
  className,
}: {
  dataKind?: string;
  live: unknown;
  histogramColor?: string;
  className?: string;
}) {
  const jsonb = useMemo(() => extractJsonb(live), [live]);
  const buckets = useMemo(
    () => (dataKind === "categorical_distribution" ? toDistribution(jsonb) : []),
    [dataKind, jsonb],
  );
  const rows = useMemo(
    () => (dataKind === "event_stream" ? toRecord(jsonb) : []),
    [dataKind, jsonb],
  );

  if (dataKind === "categorical_distribution") {
    return buckets.length > 0 ? (
      <ArraySparkline buckets={buckets} color={histogramColor} className={className} />
    ) : null;
  }
  if (dataKind === "event_stream") {
    return rows.length > 0 ? <BatchKeyValueTable rows={rows} className={className} /> : null;
  }
  return null;
}

/** True when a data_kind denotes a non-scalar (array/batch) shape. */
export function isNonScalarKind(dataKind?: string): boolean {
  return dataKind === "categorical_distribution" || dataKind === "event_stream";
}
